import { Button, ButtonProps } from "@newsela/angelou";
import { omit } from "@s-libs/micro-dash";
import React, {
  useState,
  useId,
  useCallback,
  type KeyboardEvent,
  cloneElement,
  isValidElement,
  Children,
  type HTMLAttributes,
} from "react";
import { TabsContext } from "./Tabs.context";
import { useTabsContext } from "./Tabs.context";
import { tabCss, tabListCss } from "./Tabs.styles";
import type {
  TabsProps,
  TabListProps,
  TabProps,
  TabPanelsProps,
  TabPanelProps,
} from "./Tabs.types";

const composeTabId = (tabsId: string, tabId: string) => {
  return `${tabsId}-tab-${tabId}`;
};

const composePanelId = (tabsId: string, tabId: string) => {
  return `${tabsId}-panel-${tabId}`;
};

export type TabsComponent = React.FC<TabsProps> & {
  TabList: React.FC<TabListProps>;
  Tab: React.FC<TabProps>;
  TabPanels: React.FC<TabPanelsProps>;
  TabPanel: React.FC<TabPanelProps>;
};

const Tabs: TabsComponent = ({
  children,
  defaultTab,
  selectedTab,
  onTabChange,
  ...props
}) => {
  const tabsId = `tabs-${useId()}`;
  const [internalSelectedTab, setInternalSelectedTab] = useState<string>(
    defaultTab ?? "",
  );

  const currentSelectedTab = selectedTab || internalSelectedTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (selectedTab === undefined) {
        setInternalSelectedTab(tabId);
      }
      onTabChange?.(tabId);
    },
    [selectedTab, onTabChange],
  );

  const contextValue = {
    selectedTab: currentSelectedTab,
    setSelectedTab: handleTabChange,
    tabsId,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div {...props}>{children}</div>
    </TabsContext.Provider>
  );
};

const TabList: React.FC<TabListProps> = ({ children, ...props }) => {
  const { selectedTab, setSelectedTab } = useTabsContext();
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const tabButtons = event.currentTarget.querySelectorAll(
      '[role="tab"]:not([disabled])',
    );
    const currentIndex = Array.from(tabButtons).findIndex(
      (tab) => (tab as HTMLElement).dataset["value"] === selectedTab,
    );

    let nextIndex = currentIndex;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        nextIndex = Math.min(currentIndex + 1, tabButtons.length - 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case "Home":
        event.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        event.preventDefault();
        nextIndex = tabButtons.length - 1;
        break;
      default:
        return;
    }

    const nextTab = tabButtons[nextIndex] as HTMLElement;
    setSelectedTab(nextTab.dataset["value"]!);
    nextTab.focus();
  };

  return (
    <div role="tablist" onKeyDown={handleKeyDown} css={tabListCss} {...props}>
      {children}
    </div>
  );
};

const Tab: React.FC<TabProps> = ({
  children,
  tabId,
  asChild = false,
  ...props
}) => {
  const { selectedTab, setSelectedTab, tabsId } = useTabsContext();
  const isSelected = selectedTab === tabId;

  const handleClick = () => {
    setSelectedTab(tabId);
  };

  const tabProps: Partial<ButtonProps> = {
    ...props,
    role: "tab",
    ariaProps: {
      ...props.ariaProps,
      "aria-selected": isSelected,
      "aria-controls": composePanelId(tabsId, tabId),
    },
    dataProps: {
      ...props.dataProps,
      "data-value": tabId,
      "data-selected": isSelected,
      id: composeTabId(tabsId, tabId),
      tabIndex: isSelected ? 0 : -1,
    },
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      handleClick();
      props.onClick?.(event);
    },
  };

  if (asChild && isValidElement<HTMLAttributes<HTMLElement>>(children)) {
    const onlyChild: React.ReactElement<HTMLAttributes<HTMLElement>> =
      Children.only(children);
    const { ariaProps, dataProps, ...validHtmlProps } = omit(
      tabProps,
      "__classNameFor",
      "__cssFor",
      "flavor",
      "size",
      "icon",
      "isFullWidth",
      "legacy_flavor",
      "legacy_icon",
      "legacy_size",
      "legacy_statusColor",
      "onBlur",
      "onClick",
      "onFocus",
      "onKeyDown",
      "onKeyUp",
      "onMouseEnter",
      "onMouseLeave",
      "onPointerDown",
    );
    const childProps: HTMLAttributes<HTMLElement> = {
      ...ariaProps,
      ...dataProps,
      ...validHtmlProps,
    };
    return cloneElement(onlyChild, {
      ...childProps,
      ...children.props,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        handleClick();
        children.props.onClick?.(event);
      },
    });
  }

  return (
    <Button
      flavor={Button.flavors.polarTertiary}
      __cssFor={{
        root: tabCss,
      }}
      {...tabProps}
    >
      {children}
    </Button>
  );
};

const TabPanels: React.FC<TabPanelsProps> = ({ children, ...props }) => {
  return <div {...props}>{children}</div>;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, tabId, ...props }) => {
  const { selectedTab, tabsId } = useTabsContext();
  const isSelected = selectedTab === tabId;

  if (!isSelected) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      tabIndex={0}
      id={composePanelId(tabsId, tabId)}
      aria-labelledby={composeTabId(tabsId, tabId)}
      {...props}
    >
      {children}
    </div>
  );
};

Tabs.TabList = TabList;
Tabs.Tab = Tab;
Tabs.TabPanels = TabPanels;
Tabs.TabPanel = TabPanel;

export default Tabs;
